<template>
  <BModal
    active
    class="user-bulk-upload"
    @close="cancelModal"
  >
    <header class="modal-card-head">
      <p class="modal-card-title has-text-primary">
        Facturas
      </p>
      <AppPagination
        class="is-centered"
        :current-page="currentPage"
        :buttons-max="buttonsMax"
        :pages-total="totalPages"
        @change="goToPage"
      />
    </header>

    <div class="table-container">
      <BTable
        :data="invoices"
        :checked-rows.sync="checkedRows"
        :columns="columns"
        :custom-is-checked="
          (checkedRows, invoices) => { return checkedRows.id === invoices.id }"
        checkable
      >
        <template slot-scope="props">
          <BTableColumn
            field="folio"
            label="Folio"
            internal
          >
            {{ props.row.folio }}
          </BTableColumn>
          <BTableColumn
            field="uuid"
            label="Uuid"
            internal
          >
            {{ props.row.cfdi.complement.digitalTaxStamp.uuid }}
          </BTableColumn>
          <BTableColumn
            field="total"
            label="Monto"
            internal
          >
            {{ voucherTotal(props.row.cfdi.voucherTotal) }}
          </BTableColumn>
          <BTableColumn
            field="moneda"
            label="Moneda"
            internal
          >
            {{ props.row.cfdi.voucherCurrency }}
          </BTableColumn>
          <BTableColumn
            field="receiverName"
            label="Nombre"
            internal
          >
            {{ props.row.cfdi.receiverName }}
          </BTableColumn>
          <BTableColumn
            field="fecha"
            label="Fecha"
            internal
          >
            {{ props.row.cfdi.updatedAt | toHumanDate }}
          </BTableColumn>
        </template>
        <template slot="empty">
          <section class="section">
            <div class="content has-text-grey has-text-centered">
              <p>
                <BIcon
                  icon="emoticon-sad"
                  size="is-large"
                />
              </p>
              <p>Este cliente no cuenta con facturas PPD</p>
            </div>
          </section>
        </template>
      </BTable>

      <footer class="modal-card-foot">
        <p class="control">
          <button
            :disabled="!checkedRows.length"
            class="button is-success"
            @click="closeModal(true)"
          >
            Seleccionar
          </button>
          <button
            class="button"
            type="button"
            @click="closeModal(false)"
          >
            Cerrar
          </button>
        </p>
        <AppPagination
          class="is-centered"
          :current-page="currentPage"
          :buttons-max="buttonsMax"
          :pages-total="totalPages"
          @change="goToPage"
        />
      </footer>
    </div>
  </BModal>
</template>

<script>
import merge from 'lodash/merge';

import {
  mapState,
  mapMutations,
  mapActions,
} from 'vuex';

import utils from '../../../../lib/utils';
import paymentsHelper from '../../../../lib/paymentsHelper';

export default {
  name: 'InvoiceList',
  components: {
    AppPagination: () => import(/* webpackChunkName: "app-pagination" */ '@/components/AppPagination.vue'),
  },
  data() {
    return {
      buttonsMax: 5,
      columns: [
        { field: 'folio', label: 'Folio' },
        { field: 'uuid', label: 'Uuid' },
        { field: 'total', label: 'Monto' },
        { field: 'moneda', label: 'Moneda' },
        { field: 'receiverName', label: 'Nombre' },
        { field: 'fecha', label: 'Fecha' },
      ],
      checkedRows: [],
    };
  },
  computed: merge(
    mapState({
      template: ({ invoice }) => invoice.complement.payments[0].documentsRelated[0],
      invoices: ({ invoices }) => invoices.list,
      totalPages: ({ invoices }) => invoices.totalPages,
      receiverRfc: state => state.invoice.receiverRfc,
      currentPageState: ({ invoices }) => invoices.currentPage,
    }),
    {
      lastPage() {
        return Math.ceil(this.invoices.length / this.itemsPerPage);
      },
      currentPage() {
        return parseInt(this.currentPageState, 10);
      },
      size() {
        return this.$route.query.size;
      },
    },
  ),
  watch: {
    buttonsMax(newValue, oldValue) {
      if (newValue % 2) {
        return;
      }
      if (newValue < oldValue) {
        this.buttonsMax = newValue - 1;
      }
      if (newValue > oldValue) {
        this.buttonsMax = newValue + 1;
      }
    },
  },

  async beforeMount() {
    const filters = {
      'operation.customer': this.$store.state.customers.id,
    };
    try {
      await this.search({
        filters,
        isRep: true,
        page: 1,
        size: 20,
      });
    } catch (error) {
      this.$danger({ message: 'Ocurrió un error al buscar las facturas' });
    }
  },
  methods: merge(
    mapMutations({
      addDocument: 'invoice/addDocument',
      setDocumentValue: 'invoice/updateDocument',
      setValue: 'invoices/setValue',
    }),
    mapActions({
      search: 'invoices/search',
      searchInvoices: 'invoices/searchInvoices',
    }),
    {
      async goToPage(page) {
        this.setValue({
          accessor: 'list',
          value: [],
        });
        this.setValue({
          accessor: 'currentPage',
          value: page,
        });
        const filters = {
          'operation.customer': this.$store.state.customers.id,
        };
        try {
          await this.search({
            filters,
            isRep: true,
            page,
            size: 20,
          });
        } catch (error) {
          this.$danger({ message: 'Ocurrió un error al buscar las facturas' });
        }
      },
      cancelModal() {
        this.$emit('close', true);
      },
      voucherTotal(total) {
        const string = total.toLocaleString('es-MX', {
          style: 'currency',
          currency: 'MXN',
        });

        return string;
      },
      async closeModal(select) {
        if (select) {
          const uniqueValues = new Set(this.checkedRows.map(i => i.cfdi.voucherCurrency));

          if (uniqueValues.size !== 1) {
            this.$danger({ message: 'Solo se permite seleccionar facturas con la misma moneda' });
            return;
          }
        }

        this.$emit('close', true);

        if (select) {
          this.checkedRows.forEach(async (checkedRow, numberSelected) => {
            const index = numberSelected + 1;

            const { payments } = checkedRow.cfdi.operation;
            const { totalAmount = 0 } = checkedRow.cfdi;

            const documentPreviousBalance = checkedRow.cfdi.voucherTotal - totalAmount;

            const partiality = (payments.length + 1) || 1;

            if (numberSelected > 0) {
              this.addDocument({ index: 1, document: paymentsHelper.documentTemplate('MXN') });
            }

            this.setDocumentValue({
              accessor: 'documentId',
              value: checkedRow.cfdi.complement.digitalTaxStamp.uuid,
              index,
              payment: 1,
            });

            this.setDocumentValue({
              accessor: 'documentSeries',
              value: checkedRow.cfdi.voucherSeries,
              index,
              payment: 1,
            });

            this.setDocumentValue({
              accessor: 'documentConsecutive',
              value: checkedRow.cfdi.voucherConsecutive,
              index,
              payment: 1,
            });

            this.setDocumentValue({
              accessor: 'documentPartialityNumber',
              value: partiality,
              index,
              payment: 1,
            });

            this.setDocumentValue({
              accessor: 'documentPreviousBalance',
              value: utils.fixTotals(documentPreviousBalance, 'MXN', 2),
              index,
              payment: 1,
            });

            this.setDocumentValue({
              accessor: 'documentCurrency',
              value: checkedRow.cfdi.voucherCurrency,
              index,
              payment: 1,
            });

            this.setDocumentValue({
              accessor: 'documentInvoiceExchangeRate',
              value: checkedRow.cfdi.voucherExchangeRate,
              index,
              payment: 1,
            });

            this.setDocumentValue({
              accessor: 'documentTotal',
              value: checkedRow.cfdi.voucherTotal,
              index,
              payment: 1,
            });

            this.setDocumentValue({
              accessor: 'documentTaxTransfers',
              value: paymentsHelper.createTransfersDocumentRelated(checkedRow.cfdi.concepts),
              index,
              payment: 1,
            });

            this.setDocumentValue({
              accessor: 'objectImpDr',
              value: paymentsHelper.setObjectImpDr(checkedRow.cfdi.concepts),
              index,
              payment: 1,
            });

            if (checkedRow.cfdi.taxes && checkedRow.cfdi.taxes.retained) {
              this.setDocumentValue({
                accessor: 'documentTaxWithholdings',
                value: paymentsHelper.createRetainedDocumentRelated(checkedRow.cfdi.concepts),
                index,
                payment: 1,
              });
            }

            this.$store.commit('invoice/setValue', {
              accessor: 'complement.payments[0].currency',
              value: checkedRow.cfdi.voucherCurrency,
            });
          });
        }
      },
    },
  ),
};
</script>

<style scoped>
@media screen and (min-width: 769px) {
  form {
    flex-grow: 0.5;
  }
}
</style>

<style>
.modal-card-title {
  flex-grow: 0.45;
}
.control {
  flex-grow: 0.35;
}
.user-bulk-upload .upload-draggable.is-primary {
  width: 100%;
}
</style>
